import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useForms } from '@features/forms';
import { useDebounce } from '@hooks/useDebounce';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, ButtonBase, InputBase, NativeSelect, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { FormWithRevisionSummary } from '@store/generated/forms';
import {
  formatAppointmentDuration,
  type GuidedTimingDirection,
  guidedTimingToISO,
  type GuidedTimingUnit,
  isGuidedTimingWithinWindow,
  isImmediateTiming,
  parseGuidedTiming,
} from '@utils/durationUtils';

import { EditorOptionList, PropertyEditorSearchFrame } from './PropertyEditorSearchFrame';

/**
 * Shared editors for an appointment's (or appointment type's) form and
 * reminder send rules: attach rules, schedule the same rule several times,
 * and pick preset or custom ("N minutes/hours/days/weeks before/after")
 * send timings. Used by the calendar v2 property editors and the
 * appointment-type modal.
 */

/** Lets popover hosts hear editor interactions (e.g. to pin an auto-opened popover). */
export const PropertyEditorInteractionContext = createContext<(() => void) | null>(null);

export type RuleEditorSurface = 'appointment' | 'appointment_type';

export type TimingPreset = { display: string; iso: string };

export interface AttachedRuleItem {
  id: string;
  title: string;
  offset: string;
  timing: string;
  formDefinitionId?: string;
  reminderTemplateId?: string;
  ruleId?: string | null;
}

export interface ReminderTemplateDescriptor {
  /** Value persisted as the rule's reminder_template_id — differs per surface. */
  templateId: string;
  title: string;
}

export const FORM_TIMING_PRESETS: readonly TimingPreset[] = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
  { display: '5 minutes after appointment', iso: 'PT5M' },
];

export const REMINDER_TIMING_PRESETS: readonly TimingPreset[] = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
];

export const DEFAULT_FORM_OFFSET = '-P1D';
export const DEFAULT_REMINDER_OFFSET = '-P1D';

export const formatRuleTiming = (offset: string): string => {
  if (!offset) return '';
  try {
    return formatAppointmentDuration(offset);
  } catch {
    return offset;
  }
};

// The editor rows sit under a Forms/Reminders label on an appointment, so the
// trailing "appointment" is redundant there ("1 day before appointment" ->
// "1 day before"). "at appointment time" is left intact.
const formatRuleTimingShort = (timing: string): string => timing.replace(/ appointment$/, '');

// Rules can repeat (the same form or reminder sent at several times), so each
// attached rule needs its own local identity until the server assigns one.
let localRuleIdCounter = 0;
const nextLocalRuleId = (prefix: 'form' | 'reminder'): string =>
  `${prefix}-local-${++localRuleIdCounter}`;

// Default a new send to the first preset the rule isn't already using so two
// sends never start out identical. Once every preset is taken, the fallback
// may match an existing send — accepted: the user retimes the new row, which
// beats inventing arbitrary offsets on their behalf.
const nextAvailableOffset = (
  presets: readonly TimingPreset[],
  usedOffsets: ReadonlySet<string>,
  fallback: string,
): string => presets.find(preset => !usedOffsets.has(preset.iso))?.iso ?? fallback;

export const formEntryToAttachedRuleItem = (
  entry: FormWithRevisionSummary,
): AttachedRuleItem | null => {
  const formDefinitionId = entry.form.id;
  const title = entry.current_revision?.title ?? null;
  if (!formDefinitionId || !title) return null;
  return {
    id: formDefinitionId,
    title,
    formDefinitionId,
    offset: DEFAULT_FORM_OFFSET,
    timing: formatRuleTiming(DEFAULT_FORM_OFFSET),
  };
};

const RuleOptionRow = ({
  selected,
  disabled = false,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const committedOnPointerDownRef = useRef(false);
  const onEditorInteraction = useContext(PropertyEditorInteractionContext);

  const commit = () => {
    if (disabled) return;
    onEditorInteraction?.();
    onClick();
  };

  const commitFromPress = (event: React.MouseEvent | React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (committedOnPointerDownRef.current) return;
    committedOnPointerDownRef.current = true;
    commit();
  };

  return (
    <ButtonBase
      disabled={disabled}
      onPointerDown={commitFromPress}
      onMouseDown={commitFromPress}
      onClick={event => {
        event.stopPropagation();
        if (committedOnPointerDownRef.current) {
          committedOnPointerDownRef.current = false;
          return;
        }
        commit();
      }}
      sx={{
        // Block-level flex (ButtonBase defaults to inline-flex): under an
        // editor surface's width:max-content, inline-level rows would lay out
        // side-by-side during intrinsic sizing and the popover would size to
        // the SUM of all option widths instead of the widest row (GDN-2230).
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 0.75,
        color: ELIG_TOKENS.ink,
        textAlign: 'left',
        bgcolor: selected ? 'rgba(28, 30, 133, 0.06)' : 'transparent',
        '&:hover': { bgcolor: selected ? 'rgba(28, 30, 133, 0.08)' : 'rgba(0,0,0,0.035)' },
        '&.Mui-disabled': {
          color: ELIG_TOKENS.inkFaint,
        },
      }}
    >
      {children}
    </ButtonBase>
  );
};

const RuleTitleValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: 13,
      color: ELIG_TOKENS.ink,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

const RuleEditorHint = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ px: 1.5, py: 1 }}>
    <Typography variant="caption" sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint }}>
      {children}
    </Typography>
  </Box>
);

const RuleCheckbox = ({ checked }: { checked: boolean }) => (
  <Box
    sx={{
      width: 14,
      height: 14,
      borderRadius: '3px',
      border: `1.5px solid ${checked ? ELIG_TOKENS.midnight : ELIG_TOKENS.lineStrong}`,
      bgcolor: checked ? ELIG_TOKENS.midnight : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {checked && <Box sx={{ width: 6, height: 6, borderRadius: '1px', bgcolor: '#fff' }} />}
  </Box>
);

// One fixed height keeps the quantity input and the two selects visually
// identical in the guided custom-timing row.
const RULE_TIMING_CONTROL_HEIGHT = 26;

const ruleTimingControlSx = {
  height: RULE_TIMING_CONTROL_HEIGHT,
  boxSizing: 'border-box',
  fontSize: 12,
  color: ELIG_TOKENS.ink,
  border: `1px solid ${ELIG_TOKENS.line}`,
  borderRadius: 1,
  px: 0.75,
  py: 0,
  '&.Mui-focused, &:focus-within': { borderColor: ELIG_TOKENS.midnight },
  '& input': { py: 0, height: '100%' },
  '& input[type=number]::-webkit-inner-spin-button': { display: 'none' },
  '& input[type=number]::-webkit-outer-spin-button': { display: 'none' },
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& .MuiNativeSelect-select': {
    py: 0,
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
  },
  '& .MuiNativeSelect-icon': { fontSize: 16, color: ELIG_TOKENS.inkSoft },
} as const;

const GUIDED_TIMING_UNITS: readonly GuidedTimingUnit[] = ['minutes', 'hours', 'days', 'weeks'];

interface CustomTimingDraft {
  quantity: string;
  unit: GuidedTimingUnit;
  direction: GuidedTimingDirection;
}

const DEFAULT_CUSTOM_TIMING_DRAFT: CustomTimingDraft = {
  quantity: '1',
  unit: 'days',
  direction: 'before',
};

const toCustomTimingDraft = (offset: string): CustomTimingDraft => {
  const parsed = parseGuidedTiming(offset);
  if (!parsed) return DEFAULT_CUSTOM_TIMING_DRAFT;
  return {
    quantity: String(parsed.quantity),
    unit: parsed.unit,
    direction: parsed.direction,
  };
};

const SelectedRuleRow = ({
  item,
  presets,
  disabled,
  onChangeOffset,
  onRemove,
  onDuplicate,
}: {
  item: AttachedRuleItem;
  presets: readonly TimingPreset[];
  disabled: boolean;
  onChangeOffset: (offset: string) => void;
  onRemove: () => void;
  /** Attach another send of this rule at a different time. */
  onDuplicate?: () => void;
}) => {
  const [timingOpen, setTimingOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState<CustomTimingDraft>(DEFAULT_CUSTOM_TIMING_DRAFT);
  // Blur commits only after the user has actually edited a field — an
  // untouched draft (e.g. the default seeded for an immediate offset) must
  // never overwrite the rule's stored offset just by opening and clicking away.
  const [customDraftDirty, setCustomDraftDirty] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);

  // Immediate offsets ("at appointment time") aren't representable in the
  // guided fields; treat them like presets rather than custom values.
  const isCustomTimingActive =
    Boolean(item.offset) &&
    !isImmediateTiming(item.offset) &&
    !presets.some(preset => preset.iso === item.offset);

  const toggleTimingMenu = () => {
    setTimingOpen(open => {
      const next = !open;
      if (next) {
        // Seed the guided fields from the current offset so Custom starts
        // where the rule already is (works for presets too).
        setCustomDraft(toCustomTimingDraft(item.offset));
        setCustomOpen(isCustomTimingActive);
        setCustomDraftDirty(false);
        setCustomError(null);
      }
      return next;
    });
  };

  const commitCustomDraft = (draft: CustomTimingDraft) => {
    const quantity = parseInt(draft.quantity, 10);
    if (!Number.isFinite(quantity) || quantity < 1) {
      setCustomError('Enter a number of 1 or more');
      return;
    }
    if (!isGuidedTimingWithinWindow(quantity, draft.unit)) {
      setCustomError('Scheduling window cannot exceed 30 days');
      return;
    }
    setCustomError(null);
    const offset = guidedTimingToISO({ quantity, unit: draft.unit, direction: draft.direction });
    if (offset !== item.offset) {
      onChangeOffset(offset);
    }
  };

  const updateCustomDraft = (updates: Partial<CustomTimingDraft>, commit: boolean) => {
    const next = { ...customDraft, ...updates };
    setCustomDraft(next);
    setCustomDraftDirty(true);
    if (commit) commitCustomDraft(next);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: 'rgba(28, 30, 133, 0.04)',
        }}
      >
        <ButtonBase
          disabled={disabled}
          aria-label={`Remove ${item.title}`}
          onClick={event => {
            event.stopPropagation();
            onRemove();
          }}
          sx={{ borderRadius: '3px' }}
        >
          <RuleCheckbox checked />
        </ButtonBase>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            color: ELIG_TOKENS.ink,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </Typography>
        <ButtonBase
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            toggleTimingMenu();
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            color: ELIG_TOKENS.inkSoft,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11 }}>
            {item.timing ? formatRuleTimingShort(item.timing) : 'Set timing'}
          </Typography>
          <KeyboardArrowDownOutlined
            sx={{
              fontSize: 14,
              transition: 'transform 0.15s ease',
              transform: timingOpen ? 'rotate(180deg)' : 'none',
            }}
          />
        </ButtonBase>
        {onDuplicate && (
          <ButtonBase
            disabled={disabled}
            aria-label={`Send ${item.title} again at another time`}
            title="Send again at another time"
            onClick={event => {
              event.stopPropagation();
              onDuplicate();
            }}
            sx={{
              borderRadius: 0.5,
              p: 0.25,
              color: ELIG_TOKENS.inkSoft,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </ButtonBase>
        )}
        <ButtonBase
          disabled={disabled}
          aria-label={`Remove ${item.title}`}
          title="Remove"
          onClick={event => {
            event.stopPropagation();
            onRemove();
          }}
          sx={{
            borderRadius: 0.5,
            p: 0.25,
            color: ELIG_TOKENS.inkSoft,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: 'error.main' },
          }}
        >
          <RemoveIcon sx={{ fontSize: 14 }} />
        </ButtonBase>
      </Box>
      {timingOpen && (
        <Box sx={{ pl: 4, py: 0.25, bgcolor: 'rgba(28, 30, 133, 0.02)' }}>
          {presets.map(preset => (
            <RuleOptionRow
              key={preset.iso}
              selected={item.offset === preset.iso}
              disabled={disabled}
              onClick={() => {
                onChangeOffset(preset.iso);
                setTimingOpen(false);
                setCustomOpen(false);
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, fontSize: 12, color: ELIG_TOKENS.ink }}>
                {preset.display}
              </Typography>
            </RuleOptionRow>
          ))}
          <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
          {customOpen ? (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75 }}
                onClick={event => event.stopPropagation()}
              >
                <InputBase
                  autoFocus
                  type="number"
                  value={customDraft.quantity}
                  disabled={disabled}
                  inputProps={{ min: 1, max: 999, 'aria-label': 'Amount of time' }}
                  onChange={event => updateCustomDraft({ quantity: event.target.value }, false)}
                  onBlur={() => {
                    if (customDraftDirty) commitCustomDraft(customDraft);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                  }}
                  sx={{ ...ruleTimingControlSx, width: 48 }}
                />
                <NativeSelect
                  value={customDraft.unit}
                  disabled={disabled}
                  input={<InputBase sx={ruleTimingControlSx} />}
                  inputProps={{ 'aria-label': 'Unit of time' }}
                  onChange={event =>
                    updateCustomDraft({ unit: event.target.value as GuidedTimingUnit }, true)
                  }
                >
                  {GUIDED_TIMING_UNITS.map(unit => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </NativeSelect>
                <NativeSelect
                  value={customDraft.direction}
                  disabled={disabled}
                  input={<InputBase sx={ruleTimingControlSx} />}
                  inputProps={{ 'aria-label': 'Before or after the appointment' }}
                  onChange={event =>
                    updateCustomDraft(
                      { direction: event.target.value as GuidedTimingDirection },
                      true,
                    )
                  }
                >
                  <option value="before">before</option>
                  <option value="after">after</option>
                </NativeSelect>
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint, whiteSpace: 'nowrap' }}
                >
                  appointment
                </Typography>
              </Box>
              {customError && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', px: 1.5, pb: 0.75, fontSize: 11, color: 'error.main' }}
                >
                  {customError}
                </Typography>
              )}
            </>
          ) : (
            <RuleOptionRow
              selected={isCustomTimingActive}
              disabled={disabled}
              onClick={() => setCustomOpen(true)}
            >
              <Typography variant="body2" sx={{ flex: 1, fontSize: 12, color: ELIG_TOKENS.ink }}>
                {isCustomTimingActive ? `Custom — ${item.timing}` : 'Custom...'}
              </Typography>
            </RuleOptionRow>
          )}
        </Box>
      )}
    </Box>
  );
};

export const FormsRuleEditor = ({
  value,
  disabled = false,
  surface,
  onChange,
}: {
  value: AttachedRuleItem[];
  disabled?: boolean;
  surface: RuleEditorSurface;
  onChange: (items: AttachedRuleItem[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const debouncedSearch = useDebounce(search, 250);
  const onEditorInteraction = useContext(PropertyEditorInteractionContext);
  const { appointment: appointmentTracking } = useEventTracking();
  const { forms, isLoading } = useForms({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });
  const selectedIds = new Set(value.map(item => item.formDefinitionId).filter(Boolean));
  const availableForms = forms
    .map(formEntryToAttachedRuleItem)
    .filter((item): item is AttachedRuleItem => Boolean(item?.formDefinitionId))
    .filter(item => !selectedIds.has(item.formDefinitionId));

  const commit = (items: AttachedRuleItem[]) => onChange(items);
  const addItem = (item: AttachedRuleItem) =>
    commit([
      ...value,
      {
        ...item,
        id: nextLocalRuleId('form'),
        ruleId: null,
        offset: DEFAULT_FORM_OFFSET,
        timing: formatRuleTiming(DEFAULT_FORM_OFFSET),
      },
    ]);
  const duplicateItem = (source: AttachedRuleItem) => {
    const siblingSends = value.filter(item => item.formDefinitionId === source.formDefinitionId);
    const usedOffsets = new Set(siblingSends.map(item => item.offset));
    const offset = nextAvailableOffset(FORM_TIMING_PRESETS, usedOffsets, DEFAULT_FORM_OFFSET);
    appointmentTracking.ruleAdditionalSendClicked({
      rule_type: 'form',
      surface,
      // Sends of this specific form, not the total attached-form row count.
      total_sends: siblingSends.length + 1,
    });
    commit([
      ...value,
      {
        ...source,
        id: nextLocalRuleId('form'),
        ruleId: null,
        offset,
        timing: formatRuleTiming(offset),
      },
    ]);
  };
  const removeItem = (id: string) => commit(value.filter(item => item.id !== id));
  const updateOffset = (id: string, offset: string) => {
    appointmentTracking.ruleTimingChanged({
      rule_type: 'form',
      surface,
      offset_iso: offset,
      is_custom: !FORM_TIMING_PRESETS.some(preset => preset.iso === offset),
    });
    commit(
      value.map(item =>
        item.id === id ? { ...item, offset, timing: formatRuleTiming(offset) } : item,
      ),
    );
  };

  useEffect(() => {
    setActiveIndex(0);
  }, [availableForms.length, search]);

  const commitActiveForm = () => {
    if (isLoading) return;

    const activeForm = availableForms[activeIndex];
    if (activeForm) addItem(activeForm);
  };

  return (
    <PropertyEditorSearchFrame
      search={search}
      placeholder="Search forms..."
      disabled={disabled}
      activeIndex={activeIndex}
      itemCount={!isLoading ? availableForms.length : 0}
      selectedContent={
        value.length > 0
          ? value.map(item => (
              <SelectedRuleRow
                key={item.id}
                item={item}
                presets={FORM_TIMING_PRESETS}
                disabled={disabled}
                onChangeOffset={offset => updateOffset(item.id, offset)}
                onRemove={() => removeItem(item.id)}
                onDuplicate={() => duplicateItem(item)}
              />
            ))
          : undefined
      }
      onSearchChange={setSearch}
      onInputFocus={() => onEditorInteraction?.()}
      onActiveIndexChange={setActiveIndex}
      onEnter={commitActiveForm}
    >
      {isLoading && forms.length === 0 && <RuleEditorHint>Loading forms...</RuleEditorHint>}
      {!isLoading && availableForms.length === 0 && (
        <RuleEditorHint>{search ? 'No forms match' : 'No forms available'}</RuleEditorHint>
      )}
      {availableForms.map((item, index) => (
        <RuleOptionRow
          key={item.id}
          selected={index === activeIndex}
          disabled={disabled}
          onClick={() => addItem(item)}
        >
          <RuleCheckbox checked={false} />
          <RuleTitleValue>{item.title}</RuleTitleValue>
        </RuleOptionRow>
      ))}
    </PropertyEditorSearchFrame>
  );
};

export const RemindersRuleEditor = ({
  value,
  disabled = false,
  surface,
  reminderTemplate,
  onChange,
}: {
  value: AttachedRuleItem[];
  disabled?: boolean;
  surface: RuleEditorSurface;
  reminderTemplate: ReminderTemplateDescriptor;
  onChange: (items: AttachedRuleItem[]) => void;
}) => {
  const { appointment: appointmentTracking } = useEventTracking();
  const hasReminder = value.length > 0;

  const commit = (items: AttachedRuleItem[]) => onChange(items);
  // The same reminder can be scheduled several times (e.g. 1 week, 1 day, and
  // 2 hours before), so adding stays available no matter how many sends exist.
  const addReminder = () => {
    const usedOffsets = new Set(value.map(item => item.offset));
    const offset = nextAvailableOffset(
      REMINDER_TIMING_PRESETS,
      usedOffsets,
      DEFAULT_REMINDER_OFFSET,
    );
    if (hasReminder) {
      appointmentTracking.ruleAdditionalSendClicked({
        rule_type: 'reminder',
        surface,
        total_sends: value.length + 1,
      });
    }
    commit([
      ...value,
      {
        id: nextLocalRuleId('reminder'),
        title: reminderTemplate.title,
        reminderTemplateId: reminderTemplate.templateId,
        offset,
        timing: formatRuleTiming(offset),
      },
    ]);
  };
  const removeItem = (id: string) => commit(value.filter(item => item.id !== id));
  const updateOffset = (id: string, offset: string) => {
    appointmentTracking.ruleTimingChanged({
      rule_type: 'reminder',
      surface,
      offset_iso: offset,
      is_custom: !REMINDER_TIMING_PRESETS.some(preset => preset.iso === offset),
    });
    commit(
      value.map(item =>
        item.id === id ? { ...item, offset, timing: formatRuleTiming(offset) } : item,
      ),
    );
  };

  return (
    <Box>
      {hasReminder && (
        <Box sx={{ borderBottom: `1px solid ${ELIG_TOKENS.line}`, py: 0.5 }}>
          {value.map(item => (
            <SelectedRuleRow
              key={item.id}
              item={item}
              presets={REMINDER_TIMING_PRESETS}
              disabled={disabled}
              onChangeOffset={offset => updateOffset(item.id, offset)}
              onRemove={() => removeItem(item.id)}
            />
          ))}
        </Box>
      )}
      <EditorOptionList>
        <RuleOptionRow selected={false} disabled={disabled} onClick={addReminder}>
          {hasReminder ? (
            <AddIcon sx={{ fontSize: 14, color: ELIG_TOKENS.inkSoft, flexShrink: 0 }} />
          ) : (
            <RuleCheckbox checked={false} />
          )}
          <RuleTitleValue>
            {hasReminder ? 'Add another reminder' : reminderTemplate.title}
          </RuleTitleValue>
        </RuleOptionRow>
      </EditorOptionList>
    </Box>
  );
};
